import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './app/AppRoutes.js';
import { isApiError } from './shared/api/apiError.js';
import { AuthProvider } from './shared/auth/AuthProvider.js';
import { LanguageProvider } from './shared/i18n/LanguageProvider.js';
import { ThemeProvider } from './shared/theme/ThemeProvider.js';

/**
 * Retrying a request the server deliberately refused only delays the error
 * message, so only genuine transport failures are worth a second attempt.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => !isApiError(error) && failureCount < 2,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

export function App(): ReactElement {
  return (
    <QueryClientProvider client={createQueryClient()}>
      <LanguageProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
